/**
 * AI CONTEXT: mobile-first - Aesthetically pleasing countdown timer for giveaways
 * Journal: J-20250117-01
 * 
 * This component provides:
 * - Real-time countdown display in days, hours, minutes, and seconds
 * - Glassmorphic design consistent with app aesthetic
 * - Responsive layout for mobile and desktop
 * - Noticeable but elegant visual design
 * 
 * Architecture decisions:
 * - Uses external currentTime prop for consistent timing across components
 * - Glassmorphic cards with theme colors
 * - Accessible design with proper ARIA labels
 * - Handles expired states gracefully
 * 
 * Future considerations:
 * - Add animation effects for number changes
 * - Support for different time zones
 * - Customizable styling variants
 */

import React from 'react';
import { Clock } from 'lucide-react';
import { ensureDate } from '../utils/date';

const CountdownTimer = ({ 
  endDate, 
  currentTime, 
  className = '',
  variant = 'default', // 'default' | 'compact' | 'inline'
  showIcon = true,
  expiredText = 'Giveaway Ended',
  isActive = false // Whether this is an active giveaway (for pulse animation)
}) => {
  // Calculate time remaining
  const calculateTimeRemaining = () => {
    if (!endDate || !currentTime) return null;
    
    const target = ensureDate(endDate);
    const now = ensureDate(currentTime) || new Date();
    if (!target) return { expired: false, invalid: true };

    const diff = target - now;

    if (diff <= 0) {
      return { expired: true };
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    return { days, hours, minutes, seconds, expired: false };
  };

  const timeRemaining = calculateTimeRemaining();

  if (!timeRemaining || timeRemaining.invalid) {
    return null;
  }

  if (timeRemaining.expired) {
    return (
      <div className={`flex items-center justify-center space-x-2 ${className}`}>
        <Clock className="h-4 w-4 text-theme-silver-light" />
        <span className="text-sm font-medium text-theme-silver-light">
          {expiredText}
        </span>
      </div>
    );
  }

  // Compact variant for smaller spaces
  if (variant === 'compact') {
    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        {showIcon && <Clock className="h-4 w-4 text-custom-orange" />}
        <div className="flex items-center space-x-1 text-sm">
          {timeRemaining.days > 0 && (
            <span className="font-semibold text-theme-silver">
              {timeRemaining.days}d
            </span>
          )}
          {(timeRemaining.days > 0 || timeRemaining.hours > 0) && (
            <span className="font-semibold text-theme-silver">
              {timeRemaining.hours}h
            </span>
          )}
          <span className="font-semibold text-custom-orange">
            {timeRemaining.minutes}m
          </span>
          {timeRemaining.days === 0 && (
            <span className="font-semibold text-custom-orange">
              {timeRemaining.seconds}s
            </span>
          )}
        </div>
      </div>
    );
  }

  // Inline variant for within text
  if (variant === 'inline') {
    return (
      <span className={`inline-flex items-center space-x-1 ${className}`}>
        {showIcon && <Clock className="h-3 w-3 text-custom-orange" />}
        <span className="font-semibold text-custom-orange">
          {timeRemaining.days > 0 && `${timeRemaining.days}d `}
          {(timeRemaining.days > 0 || timeRemaining.hours > 0) && `${timeRemaining.hours}h `}
          {`${timeRemaining.minutes}m`}
          {timeRemaining.days === 0 && ` ${timeRemaining.seconds}s`}
        </span>
      </span>
    );
  }

  /*
   * Default variant — an instrument readout, not a rainbow.
   * Previously days/hours were yellow and minutes/seconds were red, which put
   * four colors on one element. Now every digit is Ivory in tabular mono and
   * urgency is carried by a single Ember unit label under 24 hours.
   */
  const totalSeconds = (timeRemaining.days * 24 * 3600) + (timeRemaining.hours * 3600) + (timeRemaining.minutes * 60) + timeRemaining.seconds;
  const isUrgent = totalSeconds < 24 * 3600;

  const units = [
    { value: timeRemaining.days, label: 'Days' },
    { value: timeRemaining.hours, label: 'Hours' },
    { value: timeRemaining.minutes, label: 'Min' },
    { value: timeRemaining.seconds, label: 'Sec' },
  ];

  const pulseClass = isActive && variant === 'default' ? 'countdown-pulse' : '';

  return (
    <div
      className={`bg-theme-graphite border border-white/[0.07] p-4 ${className} ${pulseClass}`}
      role="timer"
      aria-label="Countdown timer"
    >
      {showIcon && (
        <div className="flex items-center mb-3">
          <Clock className={`h-3.5 w-3.5 mr-2 ${isUrgent ? 'text-status-closing' : 'text-theme-bronze'}`} />
          <span className={isUrgent ? 'label-tac !text-status-closing' : 'label-tac'}>
            {isUrgent ? 'Closing Soon' : 'Time Remaining'}
          </span>
        </div>
      )}

      <div className="grid grid-cols-4 gap-px bg-white/[0.07]">
        {units.map(({ value, label }) => (
          <div key={label} className="bg-theme-carbon py-2 text-center">
            <div
              className="num text-2xl font-medium text-theme-silver leading-none"
              aria-label={`${value} ${label}`}
            >
              {String(value).padStart(2, '0')}
            </div>
            <div className="mt-1.5 text-[10px] uppercase tracking-[0.16em] text-theme-silver-dark">
              {label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CountdownTimer;
